import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import Dialog from './TivigoDialog';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CollectionsRoundedIcon from '@mui/icons-material/CollectionsRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DnsRoundedIcon from '@mui/icons-material/DnsRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import SettingsEthernetRoundedIcon from '@mui/icons-material/SettingsEthernetRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import { getSetupDetailGuide } from './userPanelSetupDetailData';
import './userPanelSetupDetail.css';

const M3U_OPTIONS = [
  { id: 'full', label: 'Tüm İçerik', detail: 'Kategori, logo ve posterlerle', type: 'tvg_plus' },
  { id: 'no-icon', label: 'Logosuz', detail: 'Daha hafif kanal listesi', type: 'tvg_plus_no_icon' },
  { id: 'fresh', label: 'Fresh', detail: 'En sade ve hızlı liste', type: 'tvg_fresh' },
];

const DOWNLOAD_HINT_DURATION_MS = 3000;

const SETUP_PLATFORM_META = {
  android: { rgb: '38, 183, 103' },
  'fire-tv': { rgb: '38, 183, 103' },
  windows: { rgb: '0, 164, 239' },
  ios: { rgb: '124, 102, 255' },
  lg: { rgb: '217, 24, 91' },
  macos: { rgb: '99, 199, 255' },
  television: { rgb: '124, 102, 255' },
  satellite: { rgb: '218, 159, 14' },
  server: { rgb: '124, 102, 255' },
  playlist: { rgb: '255, 138, 31' },
};

const resolveSetupPlatform = (guide) => {
  const guideId = guide?.id || '';
  const sourceId = guide?.sourceId || guideId;

  if (SETUP_PLATFORM_META[sourceId]) return SETUP_PLATFORM_META[sourceId];
  if (['smart-iptv', 'ss-iptv', 'asx-iptv', 'smartone'].includes(guideId)) return SETUP_PLATFORM_META.television;
  if (guideId === 'televizo') return SETUP_PLATFORM_META.android;
  if (['smarters-lite', 'cloud-stream', 'iptvx-ios'].includes(guideId)) return SETUP_PLATFORM_META.ios;
  if (['xtream-plugin', 'nextstar'].includes(guideId)) return SETUP_PLATFORM_META.satellite;
  if (guideId === 'm3u') return SETUP_PLATFORM_META.playlist;
  return SETUP_PLATFORM_META.server;
};

const readStoredJson = (key) => {
  if (typeof window === 'undefined') return {};

  try {
    return JSON.parse(window.localStorage.getItem(key) || '{}');
  } catch (error) {
    return {};
  }
};

const normalizeServerUrl = (value, port = '') => {
  const rawValue = String(value || '').trim().replace(/\/$/, '');
  if (!rawValue) return '';

  const withoutProtocol = rawValue.replace(/^https?:\/\//i, '');
  const pathIndex = withoutProtocol.indexOf('/');
  const authority = pathIndex >= 0 ? withoutProtocol.slice(0, pathIndex) : withoutProtocol;
  const suffix = pathIndex >= 0 ? withoutProtocol.slice(pathIndex) : '';
  const existingPort = authority.match(/:(\d+)$/)?.[1] || '';
  const host = existingPort ? authority.slice(0, -(existingPort.length + 1)) : authority;
  const normalizedPort = String(port || existingPort || '').replace(/^:/, '');
  const httpPort = normalizedPort === '443' ? '80' : normalizedPort;

  return `http://${host}${httpPort ? `:${httpPort}` : ''}${suffix}`;
};

const getSetupSession = (userInfo) => {
  const credentials = readStoredJson('xtream_credentials');
  const storedUser = readStoredJson('xtream_user');
  const server = userInfo?.server_info || storedUser.server_info || {};
  const info = userInfo?.user_info || {};
  const serverValue = server.server_url
    || server.portal_url
    || server.url
    || credentials.server
    || info.server;
  const serverUrl = normalizeServerUrl(serverValue, server.port) || 'http://tivigo.store';

  let portalUrl = serverUrl;
  let port = String(server.port || '');

  try {
    const parsedUrl = new URL(serverUrl);
    portalUrl = `${parsedUrl.protocol}//${parsedUrl.hostname}`;
    port = parsedUrl.port || port || (parsedUrl.protocol === 'https:' ? '443' : '80');
  } catch (error) {
    const portMatch = serverUrl.match(/:(\d+)(?:\/|$)/);
    port = port || portMatch?.[1] || '80';
  }

  return {
    serverUrl,
    portalUrl,
    port,
    username: info.username || credentials.username || '',
    password: info.password || credentials.password || '',
  };
};

const copyText = async (value) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return;
    } catch (error) {
      // Güvenli olmayan originlerde aşağıdaki DOM yöntemi kullanılır.
    }
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
};

const normalizeSetupComment = (comment, index) => {
  const rawUsername = String(comment?.kullanici_adi || comment?.username || '—');
  const username = rawUsername.length <= 2
    ? rawUsername
    : `${rawUsername.charAt(0)}${'*'.repeat(Math.min(4, rawUsername.length - 2))}${rawUsername.charAt(rawUsername.length - 1)}`;
  const rawDate = comment?.olusturma_tarihi || comment?.created_at || comment?.date;
  const date = rawDate ? new Date(String(rawDate).replace(' ', 'T')) : null;

  return {
    id: comment?.id || `comment-${index}`,
    username,
    avatar: username.charAt(0).toLocaleUpperCase('tr-TR'),
    dateLabel: date && !Number.isNaN(date.getTime())
      ? new Intl.DateTimeFormat('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' }).format(date)
      : '—',
    rating: Math.max(0, Math.min(5, Number(comment?.rating) || 0)),
    text: String(comment?.yorum || comment?.text || ''),
    helpfulCount: Math.max(0, Number(comment?.helpful_count ?? comment?.helpfulCount) || 0),
  };
};

function UserPanelSetupDetail({
  device,
  userInfo,
  targetUsername = null,
  sourceAppId = null,
  onBack,
  onOpenM3u,
  onOpenReview,
  onLoadComments,
  onToggleCommentHelpful,
}) {
  const guide = useMemo(() => getSetupDetailGuide(device), [device]);
  const guideActions = [
    ...(guide?.actionUrl ? [{
      url: guide.actionUrl,
      label: guide.actionLabel,
      external: guide.actionExternal,
      download: guide.actionDownload,
    }] : []),
    ...(Array.isArray(guide?.additionalActions) ? guide.additionalActions : []),
  ].filter((action) => action?.url);
  const session = useMemo(() => getSetupSession(userInfo), [userInfo]);
  const [copiedField, setCopiedField] = useState('');
  const [m3uType, setM3uType] = useState(M3U_OPTIONS[0].id);
  const [generatedM3u, setGeneratedM3u] = useState('');
  const [guideImageIndex, setGuideImageIndex] = useState(0);
  const [imageGuideOpen, setImageGuideOpen] = useState(false);
  const [setupComments, setSetupComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState('');
  const [commentsReloadSequence, setCommentsReloadSequence] = useState(0);
  const [helpfulLoadingId, setHelpfulLoadingId] = useState(null);
  const [downloadHintVisible, setDownloadHintVisible] = useState(false);
  const downloadHintTimerRef = useRef(null);

  useEffect(() => {
    setCopiedField('');
    setM3uType(M3U_OPTIONS[0].id);
    setGeneratedM3u('');
    setGuideImageIndex(0);
    setImageGuideOpen(false);
    setDownloadHintVisible(false);
    if (downloadHintTimerRef.current) {
      window.clearTimeout(downloadHintTimerRef.current);
      downloadHintTimerRef.current = null;
    }
  }, [device, sourceAppId]);

  useEffect(() => () => {
    if (downloadHintTimerRef.current) {
      window.clearTimeout(downloadHintTimerRef.current);
    }
  }, []);

  useEffect(() => {
    let active = true;
    setSetupComments([]);
    setCommentsError('');
    if (!guide?.review) return undefined;
    if (typeof onLoadComments !== 'function') {
      setCommentsError('Yorum bağlantısı kullanılamıyor.');
      return undefined;
    }

    setCommentsLoading(true);
    Promise.resolve(onLoadComments({ pageType: 'kurulum-detay', pageRef: String(guide.id) }))
      .then((comments) => {
        if (active) setSetupComments((Array.isArray(comments) ? comments : []).map(normalizeSetupComment));
      })
      .catch((error) => {
        if (active) setCommentsError(error?.message || 'Yorumlar alınamadı.');
      })
      .finally(() => {
        if (active) setCommentsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [commentsReloadSequence, guide, onLoadComments]);

  const toggleHelpful = async (commentId) => {
    if (targetUsername || helpfulLoadingId !== null) return;
    setHelpfulLoadingId(commentId);
    setCommentsError('');
    try {
      if (typeof onToggleCommentHelpful !== 'function') throw new Error('Faydalı yorum bağlantısı kullanılamıyor.');
      await onToggleCommentHelpful(commentId);
      setCommentsReloadSequence((current) => current + 1);
    } catch (error) {
      setCommentsError(error?.message || 'Faydalı işlemi tamamlanamadı.');
    } finally {
      setHelpfulLoadingId(null);
    }
  };

  const handleCopy = async (value, field) => {
    try {
      await copyText(value);
      setCopiedField(field);
      window.setTimeout(() => setCopiedField((current) => (current === field ? '' : current)), 1500);
    } catch (error) {
      setCopiedField('');
    }
  };

  const showDownloadHint = () => {
    if (downloadHintTimerRef.current) {
      window.clearTimeout(downloadHintTimerRef.current);
    }

    setDownloadHintVisible(true);
    downloadHintTimerRef.current = window.setTimeout(() => {
      setDownloadHintVisible(false);
      downloadHintTimerRef.current = null;
    }, DOWNLOAD_HINT_DURATION_MS);
  };

  if (!guide) {
    return (
      <div className="tvup-setup-detail-page">
        <section className="tvup-setup-detail-missing">
          <button type="button" onClick={onBack}><ArrowBackRoundedIcon /> Kuruluma dön</button>
          <span>Kurulum rehberi bulunamadı</span>
          <h1>Bu uygulama için henüz bir anlatım yok.</h1>
          <p>Kurulum listesinden desteklenen bir uygulama seçebilirsiniz.</p>
        </section>
      </div>
    );
  }

  const isM3uBuilder = guide.id === 'm3u';
  const platform = resolveSetupPlatform(guide);
  const fieldDefinitions = {
    serverUrl: { id: 'server-url', label: 'Sunucu URL', value: session.serverUrl, icon: DnsRoundedIcon },
    portalUrl: { id: 'server', label: 'Sunucu URL', value: session.portalUrl, icon: DnsRoundedIcon },
    port: { id: 'port', label: 'Port', value: session.port, icon: SettingsEthernetRoundedIcon },
    username: { id: 'username', label: 'Kullanıcı Adı', value: session.username, icon: KeyRoundedIcon },
    password: { id: 'password', label: 'Şifre', value: session.password, icon: KeyRoundedIcon },
  };
  const fields = (guide.infoFields || [])
    .map((fieldId) => fieldDefinitions[fieldId])
    .filter(Boolean);
  const imageGuide = guide.imageGuide || [];
  const currentGuideImage = imageGuide[guideImageIndex] || null;
  const reviewContent = guide.review ? {
    id: guide.id,
    name: guide.name,
    logo: guide.logo,
    poster: guide.logo,
    rating: setupComments.length
      ? setupComments.reduce((total, comment) => total + comment.rating, 0) / setupComments.length
      : 0,
    commentCount: setupComments.length,
    comments: setupComments,
  } : null;
  const downloadHint = downloadHintVisible && typeof document !== 'undefined'
    ? createPortal(
      <div className="tvup-download-hint" role="status" aria-label="İndirme konumu göstergesi">
        <span className="tvup-download-hint-arrow" aria-hidden="true">
          <ArrowUpwardRoundedIcon />
        </span>
      </div>,
      document.body
    )
    : null;

  const generateM3u = () => {
    const selectedType = M3U_OPTIONS.find((option) => option.id === m3uType) || M3U_OPTIONS[0];
    const baseUrl = sourceAppId === 'smart-iptv'
      ? 'http://tvg_smartiptv.tivigo.store:80'
      : sourceAppId === 'ss-iptv'
        ? 'http://tvg_ssiptv.tivigo.store:80'
        : session.serverUrl.replace(/\/$/, '');
    const params = `username=${encodeURIComponent(session.username)}&password=${encodeURIComponent(session.password)}&type=${selectedType.type}&output=mpegts`;
    setGeneratedM3u(`${baseUrl}/get.php?${params}`);
  };

  return (
    <div className="tvup-setup-detail-page">
      <div className="tvup-setup-detail-layout">
        {isM3uBuilder ? (
          <section className="tvup-setup-detail-m3u-builder is-wide">
            <header>
              <span>PLAYLIST</span>
              <h2>M3U Bağlantısı Oluştur</h2>
              <p>İçerik tipini seçin ve uygulamanızda kullanacağınız M3U bağlantısını oluşturun.</p>
            </header>
            <div className="tvup-setup-detail-m3u-options">
              {M3U_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={m3uType === option.id ? 'is-active' : ''}
                  onClick={() => {
                    setM3uType(option.id);
                    setGeneratedM3u('');
                  }}
                >
                  <span>{option.label}</span><small>{option.detail}</small>
                </button>
              ))}
            </div>
            <button className="tvup-setup-detail-m3u-generate" type="button" onClick={generateM3u}>
              <LinkRoundedIcon /> M3U bağlantısını oluştur
            </button>
            {generatedM3u && (
              <div className="tvup-setup-detail-m3u-result">
                <span><small>Oluşturulan bağlantı</small><b>{generatedM3u}</b></span>
                <button type="button" aria-label="M3U bağlantısını kopyala" onClick={() => handleCopy(generatedM3u, 'm3u')}>
                  {copiedField === 'm3u' ? <CheckRoundedIcon /> : <ContentCopyRoundedIcon />}
                </button>
              </div>
            )}
          </section>
        ) : (
          <div className="tvup-setup-detail-guide-column">
            <section className="tvup-setup-detail-steps" aria-labelledby="tvup-setup-detail-steps-title">
            <header>
              <h2 id="tvup-setup-detail-steps-title">Kurulum Adımları</h2>
            </header>
            <ol>
              {guide.steps.map((step, index) => (
                <li key={`${guide.id}-${index}`}>
                  <span>{String(index + 1).padStart(2, '0')}</span>
                  <p>{step}</p>
                </li>
              ))}
            </ol>
            </section>
            {reviewContent && (
              <section className="tvup-setup-detail-comments" aria-label={`${guide.name} kullanıcı yorumları`}>
                <header>
                  <div>
                    <h3>Tivigo abonelerinin yorumları</h3>
                  </div>
                  <button type="button" aria-haspopup="dialog" aria-controls="tvup-review-modal" onClick={() => onOpenReview?.(reviewContent)}>
                    <RateReviewRoundedIcon />
                    Sen de Yorum Yap
                  </button>
                </header>
                <div className="tvup-setup-detail-comment-list">
                  {commentsLoading ? (
                    <p role="status">Yorumlar yükleniyor...</p>
                  ) : commentsError ? (
                    <p role="alert">{commentsError}</p>
                  ) : reviewContent.comments.length === 0 ? (
                    <p role="status">Bu uygulama için henüz yayınlanmış yorum bulunmuyor.</p>
                  ) : reviewContent.comments.map((comment) => (
                    <article key={comment.id} className="tvup-setup-detail-comment-card">
                      <span className="tvup-setup-detail-comment-avatar" aria-hidden="true">{comment.avatar}</span>
                      <div className="tvup-setup-detail-comment-body">
                        <header>
                          <div><b>{comment.username}</b><small>{comment.dateLabel}</small></div>
                          <span className="tvup-setup-detail-comment-stars" aria-label={`${comment.rating} üzerinden 5 puan`}>
                            {Array.from({ length: 5 }, (_, index) => (
                              <StarRoundedIcon key={index} className={index < comment.rating ? 'is-filled' : ''} />
                            ))}
                          </span>
                        </header>
                        <p>{comment.text}</p>
                        <footer>
                          {targetUsername ? (
                            <><ThumbUpOutlinedIcon /><span>{comment.helpfulCount} kişi faydalı buldu</span></>
                          ) : (
                            <button type="button" disabled={helpfulLoadingId !== null} onClick={() => toggleHelpful(comment.id)}>
                              <ThumbUpOutlinedIcon />
                              <span>{helpfulLoadingId === comment.id ? 'Güncelleniyor...' : `${comment.helpfulCount} kişi faydalı buldu`}</span>
                            </button>
                          )}
                        </footer>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        <aside
          className={`tvup-setup-detail-sidebar${fields.length === 0 ? ' has-no-credentials' : ''}`}
          aria-label="Uygulama ve bağlantı bilgileri"
        >
          <button className="tvup-setup-detail-card-back" type="button" onClick={onBack}>
            <ArrowBackRoundedIcon /> <span>Kurulum Merkezi</span>
          </button>
          <section
            className="tvup-setup-detail-app-card"
            style={{ '--tvup-setup-platform-rgb': platform.rgb }}
          >
            <div className="tvup-setup-detail-app-identity">
              <span className={`tvup-setup-detail-app-logo${guide.official ? ' is-official' : ''}`}>
                <img src={guide.logo} alt="" />
              </span>
              <div className="tvup-setup-detail-app-copy">
                <h1>{guide.name}</h1>
                <p>{guide.description}</p>
              </div>
            </div>
            {targetUsername && (
              <span className="tvup-setup-detail-account"><i /> Kurulum yapılan hesap <b>{targetUsername}</b></span>
            )}
            {!isM3uBuilder && (
              <div className="tvup-setup-detail-app-meta">
                <span><small>Yükleme</small><b>{guide.loading}</b></span>
                <span><small>Adım</small><b>{guide.steps.length}</b></span>
              </div>
            )}
            {guideActions.length > 0 && (
              <div className={`tvup-setup-detail-actions${guideActions.length > 1 ? ' has-multiple' : ''}`}>
                {guideActions.map((action) => (
                  <a
                    className={`tvup-setup-detail-action${action.download ? ' is-download' : ''}`}
                    href={action.url}
                    target={action.external ? '_blank' : undefined}
                    rel={action.external ? 'noreferrer' : undefined}
                    download={action.download ? '' : undefined}
                    onClick={action.download ? showDownloadHint : undefined}
                    key={`${action.label}-${action.url}`}
                  >
                    <span>{action.label}</span>
                    {action.download ? <FileDownloadRoundedIcon /> : <LaunchRoundedIcon />}
                  </a>
                ))}
              </div>
            )}
            {imageGuide.length > 0 && (
              <button
                className="tvup-setup-detail-image-action"
                type="button"
                onClick={() => {
                  setGuideImageIndex(0);
                  setImageGuideOpen(true);
                }}
              >
                <CollectionsRoundedIcon />
                <span>Resimli Anlatım</span>
                <ChevronRightRoundedIcon />
              </button>
            )}
          </section>

          {fields.length > 0 && (
            <section className="tvup-setup-detail-credentials">
              <header>
                <h2>{guide.id === 'server-info' ? 'Sunucu Bilgilerim' : 'Uygulama Giriş Bilgileri'}</h2>
              </header>
              <div>
                {fields.map((field) => {
                  const FieldIcon = field.icon;
                  return (
                    <button key={field.id} type="button" onClick={() => handleCopy(field.value, field.id)}>
                      <span><FieldIcon /></span>
                      <span><small>{field.label}</small><b>{field.display || field.value}</b></span>
                      {copiedField === field.id ? <CheckRoundedIcon className="is-copied" /> : <ContentCopyRoundedIcon />}
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {guide.hasM3u && !isM3uBuilder && (
            <section className="tvup-setup-detail-m3u-note">
              <LinkRoundedIcon />
              <span><b>M3U Dosyanız</b><small>Uygulamaya özel M3U bağlantınızı oluşturun.</small></span>
              <button type="button" onClick={() => onOpenM3u?.(guide.sourceId || guide.id)}>M3U Dosyanız</button>
            </section>
          )}
        </aside>
      </div>

      {downloadHint}

      <Dialog
        open={imageGuideOpen}
        maxWidth={false}
        keepMounted
        transitionDuration={0}
        disableEscapeKeyDown
        className="tvup-setup-image-dialog"
        aria-labelledby="tvup-setup-image-modal-title"
        slotProps={{
          backdrop: { className: 'tvup-setup-image-backdrop' },
          paper: { id: 'tvup-setup-image-modal', className: 'tvup-setup-image-modal' },
        }}
      >
        <header className="tvup-setup-image-header">
          <span className="tvup-setup-image-title-icon" aria-hidden="true"><CollectionsRoundedIcon /></span>
          <div>
            <h2 id="tvup-setup-image-modal-title">Resimli Anlatım</h2>
            <p>{guide.name} · {guideImageIndex + 1} / {imageGuide.length}</p>
          </div>
          <button type="button" aria-label="Resimli anlatımı kapat" onClick={() => setImageGuideOpen(false)}>
            <CloseRoundedIcon />
          </button>
        </header>

        <div className="tvup-setup-image-content">
          {currentGuideImage && (
            <img
              key={currentGuideImage}
              src={currentGuideImage}
              alt={`${guide.name} kurulum adımı ${guideImageIndex + 1}`}
            />
          )}
        </div>

        <footer className="tvup-setup-image-footer">
          <button
            type="button"
            disabled={guideImageIndex === 0}
            onClick={() => setGuideImageIndex((current) => Math.max(0, current - 1))}
          >
            <ChevronLeftRoundedIcon /> Önceki
          </button>
          <span><b>{guideImageIndex + 1}</b> / {imageGuide.length}</span>
          <button
            type="button"
            disabled={guideImageIndex === imageGuide.length - 1}
            onClick={() => setGuideImageIndex((current) => Math.min(imageGuide.length - 1, current + 1))}
          >
            Sonraki <ChevronRightRoundedIcon />
          </button>
        </footer>
      </Dialog>
    </div>
  );
}

export default UserPanelSetupDetail;
